import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {FullscreenService} from '../../../services/fullscreen/fullscreen.service';
import {ExamService} from '../../../services/exam/exam.service';
import {ActivatedRoute, Router} from '@angular/router';
import { interval, Subscription } from 'rxjs';
import {LangSrcService} from '../../../services/lang-src/lang-src.service';
import {CommonModule} from '@angular/common';
import {NgbProgressbar} from '@ng-bootstrap/ng-bootstrap';
import {FirstListeningComponent} from '../../../components/exam/listening/first-listening/first-listening.component';
import {SecondListeningComponent} from '../../../components/exam/listening/second-listening/second-listening.component';
import {ThirdListeningComponent} from '../../../components/exam/listening/third-listening/third-listening.component';
import {FourthListeningComponent} from '../../../components/exam/listening/fourth-listening/fourth-listening.component';
import {FirstReadingComponent} from '../../../components/exam/reading/first-reading/first-reading.component';
import {SecondReadingComponent} from '../../../components/exam/reading/second-reading/second-reading.component';
import {ThirdReadingComponent} from '../../../components/exam/reading/third-reading/third-reading.component';
import {ScrollService} from "../../../services/scroll/scroll.service";
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-exam-exam',
  standalone: true,
  imports: [
    CommonModule,
    NgbProgressbar,
    FirstListeningComponent,
    SecondListeningComponent,
    ThirdListeningComponent,
    FourthListeningComponent,
    FirstReadingComponent,
    SecondReadingComponent,
    ThirdReadingComponent,
    TranslateModule
  ],
  templateUrl: './exam-exam.component.html',
  styleUrls: ['./exam-exam.component.scss']
})
export class ExamExamComponent implements OnInit, OnDestroy {

  examResult: any;
  timeLeft: string;
  subscription: Subscription;
  step: number;
  idLevel = this.route.snapshot.params.idLevel;
  ending = false;

  seconds = 0;
  maxSeconds = 15;

  constructor(
    private fullscreenSrv: FullscreenService,
    private examSrv: ExamService,
    public route: ActivatedRoute,
    public langSrcSrv: LangSrcService,
    private router: Router,
    private scrollTo: ScrollService,
    private zone: NgZone
  ) { }

  async ngOnInit(): Promise<void> {
    this.fullscreenSrv.open();
    this.examResult = await this.examSrv.findResultById(this.route.snapshot.params.idLevel);
    if (this.examResult.status === 'FINISHED') {
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'exam', this.route.snapshot.params.idLevel, 'result']);
    }

    this.updateStep();

    this.subscription = interval(1000).subscribe(() => {
      this.updateCounter();
    });
  }

  async ngOnDestroy() {
    this.fullscreenSrv.close();
  }

  updateStep(): void {
    switch (this.examResult.status) {
      case 'FIRST_LISTENING' :
        this.step = 1;
        break;
      case 'SECOND_LISTENING' :
        this.step = 2;
        break;
      case 'THIRD_LISTENING' :
        this.step = 3;
        break;
      case 'FOURTH_LISTENING' :
        this.step = 4;
        break;
      case 'FIRST_READING' :
        this.step = 5;
        break;
      case 'SECOND_READING' :
        this.step = 6;
        break;
      case 'THIRD_READING' :
        this.step = 7;
        break;
      default :
        this.step = 1;
        break;
    }
  }

  private updateCounter(): void {
    const now = new Date();
    const date = new Date(this.examResult.dateStart);
    date.setHours(date.getHours() + 2);
    const difference = date.getTime() - now.getTime();

    if (difference <= 0) {
      this.timeLeft = '00:00:00';
      this.subscription.unsubscribe();

      this.onTimerEnd();

      return;
    }

    const hours = Math.floor((difference % (1000 * 3600 * 24)) / (1000 * 3600));
    const minutes = Math.floor((difference % (1000 * 3600)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    this.timeLeft = `${this.pad(hours)} : ${this.pad(minutes)} : ${this.pad(seconds)}`;
  }

  private pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  async next(data: string) {
    switch (data) {
      case 'FIRST_LISTENING':
        this.examResult.status = 'SECOND_LISTENING';
        break;
      case 'SECOND_LISTENING':
        this.examResult.status = 'THIRD_LISTENING';
        break;
      case 'THIRD_LISTENING':
        this.examResult.status = 'FOURTH_LISTENING';
        break;
      case 'FOURTH_LISTENING':
        this.examResult.status = 'FIRST_READING';
        break;
      case 'FIRST_READING':
        this.examResult.status = 'SECOND_READING';
        break;
      case 'SECOND_READING':
        this.examResult.status = 'THIRD_READING';
        break;
      case 'THIRD_READING':
        this.examResult.status = 'FINISHED';
        await this.onTimerEnd();
        break;
    }
    this.updateStep();
    this.scrollTo.scrollTo({
      target: 'exam-header'
    });
  }

  async onTimerEnd(): Promise<void> {
    this.examResult = await this.examSrv.end(this.route.snapshot.params.idLevel).then(() => {
      this.ending = true;
      setTimeout(() =>
        {
          if (this.seconds > this.maxSeconds) {
            this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'exam', this.route.snapshot.params.idLevel, 'result']);
          } else {
            this.zone.run(() => {
              this.seconds = this.seconds + 2.5;
            });
            this.onTimerEnd();
          }
        },
        100);
    });
  }
}
